(ns bec-cljs-common.utils.interop
  (:require [bec-cljs-common.config :as config]
            [bec-cljs-common.utils.formatting :as fmt]
            [clojure.string :as s]
            [taoensso.timbre :as log]
            [cljs.reader :refer [read-string]]
            [cljs.core.async :as async]
            [cljs.core.async.interop :refer-macros [<p!]]))

(let [timeouts (atom {})]
  (defn set-timeout [{:keys [id callback ms action]}]
    (let [existing (get @timeouts id)]
      (if (not existing)
        (when (fn? callback)
          (swap! timeouts assoc id (js/setTimeout (fn [] (callback) (swap! timeouts dissoc id)) ms)))
        (when (and (contains? #{:cancel :remove :stop :end} action) existing)
          (js/clearTimeout existing)
          (swap! timeouts dissoc id))))))

(let [live-intervals (atom {})]
  (defn set-interval [{:keys [action id ms callback]}]
    (let [existing (get @live-intervals id)]
      (if (not existing)
        (swap! live-intervals assoc id (js/setInterval callback ms))
        (when (and (contains? #{:cancel :remove :stop :end} action) existing)
          (js/clearInterval existing)
          (swap! live-intervals dissoc id))))))

(defn get-element-by-id [id]
  (if (string? id)
    (let [element (js/document.getElementById id)]
      (if (nil? element)
        (log/fatal "Element with id; " id " was not found")
        element))
    (log/warn "Type error! Id must be a string; " id)))

(defn scroll-to
  ([{:keys [element element-id id x y]}]
   (let [x (if (> x 0) x 0)
         y (if (> y 0) y 0)
         id (or id element-id)
         element (or element (when id (get-element-by-id id)))]
     (scroll-to element x y)))
  ([element x y]
   (let [x (if (> x 0) x 0)
         y (if (> y 0) y 0)
         id (when (string? element) element)
         element (or (when id (get-element-by-id id)) element)]
     (if element
       (try
         (.scrollTo element x y)
         (catch js/Error e
           (log/warn "Given element: " element)
           (log/warn "Given x, y: " x ", " y)
           (log/warn e
             {:id id :element element :x x :y y})))
       (log/warn "Can not scroll to position on element; " element ", with id: " id)))))

(defn scroll-by
  ([{:keys [element element-id id top left behavior]}]
   (let [x (if (> left 0) left 0)
         y (if (> top 0) top 0)
         id (or id element-id)
         element (or element (when id (get-element-by-id id)))]
     (scroll-by element x y behavior)))
  ([element x y behavior]
   (let [x (if (> x 0) x 0)
         y (if (> y 0) y 0)
         id (when (string? element) element)
         element (or (when id (get-element-by-id id)) element)]
     (if element
       (try
         (.scrollBy element (clj->js {:left x :top y :behavior behavior}))
         (catch js/Error e
           (log/warn "Given element: " element)
           (log/warn "Given x, y: " x ", " y)
           (log/warn e
             {:id id :element element :x x :y y})))
       (log/warn "Can not scroll by x: "x" and y: "y" on element; " element ", with id: " id)))))

(defn scroll-into-view
  ([props]
   (if (map? props)
     (let
      [options (or (:options props) {:behavior "smooth" :block "center" :inline "center"})
       id (or (:id props) (:element-id props))
       element (or (:element props) (when id (get-element-by-id id)))]
       (scroll-into-view element options))
     (let [options {:behavior "smooth" :block "center" :inline "center"}
           id (when (string? props) props)
           element (or (when id (get-element-by-id id)) props)]
       (scroll-into-view element options))))
  ([element options]
   (let [options (or options {:behavior "smooth" :block "center" :inline "center"})
         id (when (string? element) element)
         element (or (when id (get-element-by-id id)) element)]
     (if element
       (try
         (.scrollIntoView element (clj->js options))
         (catch js/Error e
           (log/warn e
             {:id id :element element :options options})))
       (log/warn "Can not scroll into view on element; " element ", with id: " id)))))

(defn set-scroll-position
  ([{:keys [element element-id id x y]}]
   (let [x (if (> x 0) x 0)
         y (if (> y 0) y 0)
         id (or id element-id)
         element (or element (when id (get-element-by-id id)))]
     (set-scroll-position element x y)))
  ([element x y]
   (let [x (if (> x 0) x 0)
         y (if (> y 0) y 0)
         id (when (string? element) element)
         element (or (when id (get-element-by-id id)) element)]
     (if element
       (try
         (do (set! (.-scrollTop element) y)
             (set! (.-scrollLeft element) x))
         (catch js/Error e
           (log/warn e
             {:element element :x x :y y})))
       (log/warn "Can not set scroll position on element; " element ", with id; " id)))))

(defn set-url [url]
  (when url (set! (.. js/window -location -href) url)))

(defn open-url [url behaviour]
  (when url (.open js/window url (or behaviour "_blank"))))

(defn set-anchor [anchor]
  (when anchor (set! (.. js/window -location -hash) anchor)))

(defn add-event-listener
  ([{:keys [element id type callback options]}]
   (let [options (or (js->clj options) false)
         element (or element (when id (get-element-by-id id)) js/window)]
     (if (and (string? type) (fn? callback) element)
       (try
         (.addEventListener element type callback options)
         (catch js/Error e
           (log/warn e
             {:element element :type type :callback callback :options options})))
       (log/warn "Type error! Check that the inputs have the correct types."
         {:element element :type type :callback callback :options options}))))
  ([element type callback]
   (if (and (string? type) (fn? callback) element)
     (try
       (.addEventListener element type callback)
       (catch js/Error e
         (log/warn e
           {:element element :type type :callback callback})))
     (log/warn "Type error! Check that the inputs have the correct types."
       {:element element :type type :callback callback})))
  ([element type callback options]
   (let [options (or (js->clj options) false)]
     (if (and (string? type) (fn? callback) element)
       (try
         (.addEventListener element type callback options)
         (catch js/Error e
           (log/warn e
             {:element element :type type :callback callback :options options})))
       (log/warn "Type error! Check that the inputs have the correct types."
         {:element element :type type :callback callback :options options})))))

(defn focus [props]
  (let [id (or (when (map? props) (:id props)) (when (string? props) props))
        element (or (when (map? props) (:element props))
                  (when id (get-element-by-id id))
                  (when (and (not id) (not (map? props))) props))]
    (if element
      (try
        (.focus element)
        (catch js/Error e
          (log/warn e
            (if id
              (str "Given element id: " id)
              (str "Given element: " element)))))
      (log/warn "Can not focus element; " element ", with id; " id))))

(defn create-canvas []
  (try
    (. js/document (createElement "canvas"))
    (catch js/Error e
      (log/warn e))))

(defn video->img [element & [props]]
  (when-let [video (if (string? element) (get-element-by-id element) element)]
    (let [canvas (create-canvas)
          ctx (when canvas (.getContext canvas "2d"))
          filter (or (:filter (:style props)) (.-filter (.-style video)))
          height (or (.-videoHeight video) 0)
          width (or (.-videoWidth video) 0)
          offset (- width height)]
      (when canvas
        (.pause video)
        (when-let [video-source (.-srcObject video)]
          (.stop (first (.getVideoTracks video-source)))
          (set! (.-width canvas) width)
          (set! (.-height canvas) height)
          (set! (.-filter ctx) filter)
          (.translate ctx height 0)
          (.scale ctx -1 1)
          (.drawImage ctx video (- offset) 0 width height)
          (.toDataURL canvas))))))

(defn svg->url [element]
  (try
    (when-let [svg-element (if (string? element) (get-element-by-id element) element)]
      (let [cloned-svg-element (.cloneNode svg-element true)
            outer-HTML (.-outerHTML cloned-svg-element)
            blob (new js/Blob [outer-HTML] (clj->js {:type "image/svg+xml;charset=utf-8"}))
            URL (.-URL js/window)
            blob-URL (.createObjectURL URL blob)]
        blob-URL))
    (catch js/Error e
      (log/warn e)
      nil)))

(defn img->data-url
  ([props]
   (let [image-id (if (string? props) props (or (:image-id props) (:img-id props) (:id props)))]
     (img->data-url image-id (:width props) (:height props))))
  ([element size]
   (img->data-url element size size))
  ([element width height]
   (let [image-id (when (string? element) element)
         image (if image-id (get-element-by-id image-id) element)
         canvas (create-canvas)
         ctx (when canvas (.getContext canvas "2d"))
         height (or height (.-height image) 0)
         width (or width (.-width image) 0)
         offset (- width height)]
     (when (and image canvas ctx)
       (set! (.-width canvas) width)
       (set! (.-height canvas) height)
       (.translate ctx height 0)
       (.scale ctx 1 1)
       (.drawImage ctx image offset 0 (- width) height)
       (.toDataURL canvas)))))

(defn img-src->data-url
  ([src] (img-src->data-url src nil))
  ([src props]
   (try
     (when (string? src)
       (let [image-element (new js/Image)]
         (set! (.-src image-element) src)
         (set! (.-onload image-element)
           (fn []
             (let [on-load-callback (:on-load props)]
               (when (fn? on-load-callback)
                 (let [canvas (create-canvas)
                       ctx (when canvas (.getContext canvas "2d"))]
                   (when canvas
                     (let [width (or (.-width image-element) (:width (:style props)) (:width props) 0)
                           height (or (.-height image-element) (:height (:style props)) (:height props) 0)
                           offset (- width height)]
                       (set! (.-width canvas) width)
                       (set! (.-height canvas) height)
                       (set! (.-filter ctx) (or (:filter (:style props)) (:filter props)))
                       (.translate ctx height 0)
                       (.scale ctx 1 1)
                       (.drawImage ctx image-element offset 0 (- width) height)))
                   (on-load-callback src (.toDataURL canvas)))))))))
     (catch js/Error e
       (log/warn e)
       nil))))

(defn local-storage ;; NOTE: For e.g. DGS and SGS, the cache is cleared out on startup via .xinitrc - which means this stuff isn't persisted after all and behaves more like sessionStorage. - lnostdal
  ([]
   (read-string (js/window.localStorage.getItem (str (fmt/hyphenated-to-camel-case config/app-type) "DB"))))
  ([action]
   (when (= action :clear)
     (js/window.localStorage.clear)))
  ([action k]
   (case action
     :set-item (local-storage action k nil)
     :get-item (get (read-string (js/window.localStorage.getItem
                                   (str (fmt/hyphenated-to-camel-case config/app-type) "DB"))) k)
     :remove-item (let [local-storage-db (read-string (js/window.localStorage.getItem (str config/app-type "-db")))
                        new-local-storage-db (dissoc local-storage-db k)]
                    (js/window.localStorage.setItem
                      (str (fmt/hyphenated-to-camel-case config/app-type) "DB") new-local-storage-db))
     nil))
  ([action k v]
   (when (= action :set-item)
     (let [local-storage-db (read-string (js/window.localStorage.getItem
                                           (str (fmt/hyphenated-to-camel-case config/app-type) "DB")))
           new-local-storage-db (merge local-storage-db {k v})]
       (js/window.localStorage.setItem
         (str (fmt/hyphenated-to-camel-case config/app-type) "DB") (pr-str new-local-storage-db))))))
